import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: [
          "bg-primary text-primary-foreground",
          "hover:bg-primary/90 hover:shadow-lg",
          "dark:hover:shadow-[0_4px_20px_-2px_hsl(160_84%_39%/0.4)]",
          "shadow-[0_2px_8px_-2px_hsl(160_84%_32%/0.3)]",
        ].join(" "),
        destructive: [
          "bg-destructive text-destructive-foreground",
          "hover:bg-destructive/90 hover:shadow-lg",
          "dark:hover:shadow-[0_4px_20px_-2px_hsl(0_84%_60%/0.4)]",
          "shadow-[0_2px_8px_-2px_hsl(0_78%_52%/0.3)]",
        ].join(" "),
        outline: [
          "border-2 border-input bg-background",
          "hover:bg-accent hover:text-accent-foreground hover:border-accent",
          "dark:border-border/50 dark:hover:border-accent",
        ].join(" "),
        secondary: [
          "bg-secondary text-secondary-foreground",
          "hover:bg-secondary/80",
          "dark:bg-secondary/80 dark:hover:bg-secondary",
          "shadow-sm hover:shadow-md",
        ].join(" "),
        ghost: [
          "hover:bg-accent/10 hover:text-accent-foreground",
          "dark:hover:bg-accent/20",
        ].join(" "),
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
